import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { CircularProgress } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Divider from '@material-ui/core/Divider'
import IconButton from '@material-ui/core/IconButton'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import CloseIcon from '@material-ui/icons/Close'
import clsx from 'clsx'
import React from 'react'
export interface ConfirmationDialogStypeProps {
    /**
     * Whethere to remove borders from top and bottom of footer and header respectively
     */
    removeHeaderBorder?: boolean
    removeFooterBorder?: boolean
    /**
     * To determine color of title in header
     */
    headerColor?: string
    isForAssociation?: boolean
}
const useStyles = makeStyles((theme: Theme) => ({
    dialogTitle: {
        '& .MuiTypography-h6': {
            fontSize: pxToRem(22),
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        '&.MuiDialogTitle-root': {
            paddingLeft: (props: ConfirmationDialogStypeProps) =>
                theme.spacing(props.isForAssociation ? 3 : 0),
            paddingRight: theme.spacing(0),
        },
        borderBottom: (props: ConfirmationDialogStypeProps) =>
            !props.removeHeaderBorder ? '1px solid #A7ACA8' : 'none',
    },
    dialogTitlePadding: {
        paddingBottom: theme.spacing(1),
        paddingTop: theme.spacing(1),
        paddingLeft: theme.spacing(3),
    },
    forAssociation: {
        paddingBottom: theme.spacing(2),
        paddingTop: theme.spacing(2),
        paddingLeft: theme.spacing(3),
        maxWidth: 1020,
    },
    dialogCont: {
        color: theme.palette.common.black,
        '@media (max-width: sm)': {
            width: 250,
        },
        '&.MuiDialogContent-root': {
            paddingLeft: (props: ConfirmationDialogStypeProps) =>
                theme.spacing(props.isForAssociation ? 3 : 0),
            paddingRight: (props: ConfirmationDialogStypeProps) =>
                theme.spacing(props.isForAssociation ? 3 : 0),
        },
        fontSize: pxToRem(18),
        borderBottom: (props: ConfirmationDialogStypeProps) =>
            !props.removeFooterBorder ? '1px solid #A7ACA8' : 'none',
    },
    addContentOverflow: {
        overflowY: 'auto',
    },
    dialogContSmall: {
        '&.MuiDialogContent-root': {
            width: pxToRem(700),
            height: pxToRem(550),
        },
    },
    dialogContDefault: {
        '&.MuiDialogContent-root': {
            width: pxToRem(500),
            height: 'auto',
        },
    },

    actionButton: {
        color: '#fff',
        fontSize: pxToRem(18),
        width: 108,
        background: '#d3455b',
        marginBottom: theme.spacing(0.5),
        '&:hover': {
            backgroundColor: '#e9a2ad',
        },
    },
    actionButtonImportOnly: {
        color: '#A78966',
        fontSize: pxToRem(18),
        width: 130,
        marginBottom: theme.spacing(16),
    },
    confirmationText: {
        paddingTop: theme.spacing(2),
        fontSize: pxToRem(18),
        color: theme.palette.common.black,
    },
    warningDialogCont: {
        '& .MuiDialog-paperScrollPaper': {
            paddingLeft: (props: ConfirmationDialogStypeProps) =>
                theme.spacing(props.isForAssociation ? 0 : 3),
            paddingRight: (props: ConfirmationDialogStypeProps) =>
                theme.spacing(props.isForAssociation ? 0 : 3),
            maxHeight: '95vh',
        },
    },
    dialogAction: {
        '&.MuiDialogActions-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
        },
    },
    detailText: {
        fontSize: pxToRem(11),
    },
    circularProgressCenter: {
        position: 'absolute',
        left: '50%',
        top: '50%',
    },
    marginAuto: {
        margin: 'auto',
    },
}))

export interface ConfirmationDialogProps extends ConfirmationDialogStypeProps {
    /**
     * Defines state of confirmation dialog.
     */
    openDialog: boolean

    /**
     * For closing confirmation dialog
     */
    handleDialogClose?: VoidFunction

    /**
     * Trigger action on confirmation click
     */
    dialogActionCallback: (_: React.MouseEvent<Element, MouseEvent>) => void
    /**
     * Defines the heading of the confirmation dialog. Default: Confirm
     */
    dialogHeading?: string

    /**
     * Defines the content for the confirmation dialog.
     */
    contentNode?: React.ReactNode

    /*
     * Defines the confirmation message placed above action buttons on the confirmation dialog. Default: Are you sure you want to proceed?
     */
    confirmMessage?: string

    /*
     * Defines the text for cancel action button on confirmation dialog. Default: Cancel
     */
    cancelText?: string

    /*
     * Defines the text for confirmation action button  on confirmation dialog. Default: Ok
     */
    confirmText?: string

    /**
     * Defines the size for the confirmation dialog.
     */
    size?: 'small' | 'default' | 'large'
    /**
     * Top and bottom spacing for  title text on header
     */
    titlePadding?: boolean
    /**
     * Top  spacing for  main content item
     */
    /**
     * For setting overflow in dialog of large size
     */
    contentOverflow?: boolean
    /**
     * For setting overflow in dialog of large size
     */
    calledForImportExecution?: boolean
    loading?: boolean
    dialogActionSuccessCallback?: VoidFunction
    disabled?: boolean
    auditDetails: AuditType
}

interface AuditType {
    sourceProductCount?: number
    matchPropertiesCount?: number
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
    openDialog,
    handleDialogClose,
    dialogActionCallback,
    dialogActionSuccessCallback,
    dialogHeading = 'Move or copy a product',
    contentNode,
    confirmMessage = '',
    cancelText = 'Copy',
    confirmText = 'Move',
    size,
    removeHeaderBorder,
    removeFooterBorder,
    headerColor = '#EB5757',
    titlePadding,
    isForAssociation,
    contentOverflow,
    calledForImportExecution,
    loading,
    disabled,
    auditDetails,
}) => {
    const classes = useStyles({
        removeFooterBorder,
        removeHeaderBorder,
        headerColor,
        isForAssociation,
    })
    const { sourceProductCount = 0, matchPropertiesCount = 0 } = auditDetails
    return (
        <Dialog
            open={openDialog}
            data-testid="projectRemovalWarning"
            className={classes.warningDialogCont}
            aria-labelledby="remove-project-properties"
            maxWidth="lg"
        >
            <DialogTitle
                className={clsx(
                    classes.dialogTitle,
                    titlePadding && classes.dialogTitlePadding,
                    isForAssociation && classes.forAssociation
                )}
            >
                {dialogHeading}
                <IconButton onClick={handleDialogClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <Divider variant="fullWidth" />

            <DialogContent
                className={clsx(
                    classes.dialogCont,
                    contentOverflow && classes.addContentOverflow,
                    size === 'small' && classes.dialogContSmall,
                    size === 'default' && classes.dialogContDefault,
                    classes.marginAuto
                )}
            >
                {contentNode}
            </DialogContent>
            {!removeFooterBorder && <Divider variant="fullWidth" />}
            <Typography
                className={clsx(
                    confirmMessage?.length !== 0 && classes.confirmationText
                )}
            >
                {confirmMessage}
            </Typography>
            <DialogActions className={classes.dialogAction}>
                <div data-testid="auditDataText">
                    <Typography className={classes.detailText}>
                        {matchPropertiesCount} of {sourceProductCount}{' '}
                        properties and their options will automatically transfer
                        during the move or copy.
                    </Typography>
                    <Typography className={classes.detailText}>
                        {matchPropertiesCount} = Number of product type
                        properties that have a selected option in the source
                        product and at least <br /> one match with the options
                        in the destination product type.
                    </Typography>
                    <Typography className={classes.detailText}>
                        {sourceProductCount} = Number of product type properties
                        in the source product.
                    </Typography>
                </div>
                <Button
                    data-testid="cancelRemoveButton"
                    className={classes.actionButton}
                    onClick={dialogActionSuccessCallback}
                    disabled={disabled || loading}
                >
                    {cancelText}
                </Button>
                <Button
                    data-testid="confirmRemoveButton"
                    onClick={dialogActionCallback}
                    disabled={disabled || loading}
                    className={
                        calledForImportExecution
                            ? classes.actionButtonImportOnly
                            : classes.actionButton
                    }
                >
                    {confirmText}
                </Button>
            </DialogActions>
            {loading && (
                <CircularProgress className={classes.circularProgressCenter} />
            )}
        </Dialog>
    )
}

export default ConfirmationDialog
